import {
  collection,
  onSnapshot,
  orderBy,
  query,
  getDocs,
  DocumentData,
  QueryDocumentSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';

export interface PushNotificationRecord {
  id: string;
  title: string;
  body: string;
  topic: string;
  route?: string | null;
  ctaLabel?: string | null;
  imageUrl?: string | null;
  previewText?: string | null;
  data?: Record<string, any> | null;
  sentAt: Date;
  sentBy?: string;
  messageId?: string;
  targetedUserId?: string | null;
  targetedTokens?: number | null;
  successCount?: number;
  failureCount?: number;
}

export interface BroadcastPushPayload {
  title: string;
  body: string;
  route?: string;
  ctaLabel?: string;
  imageUrl?: string;
  previewText?: string;
  data?: Record<string, any>;
  topic?: string;
}

export interface UserPushPayload {
  userId: string;
  title: string;
  body: string;
  route?: string;
  data?: Record<string, any>;
}

const COLLECTION = 'push_notifications';

const mapPushNotification = (
  docSnap: QueryDocumentSnapshot<DocumentData>
): PushNotificationRecord => {
  const data = docSnap.data();
  const sentAt = convertToDate(data.sentAt);

  return {
    id: docSnap.id,
    title: data.title,
    body: data.body,
    topic: data.topic ?? 'all',
    route: data.route ?? null,
    ctaLabel: data.ctaLabel ?? null,
    imageUrl: data.imageUrl ?? null,
    previewText: data.previewText ?? null,
    data: data.data ?? null,
    sentAt,
    sentBy: data.sentBy,
    messageId: data.messageId,
    targetedUserId: data.targetedUserId ?? null,
    targetedTokens:
      typeof data.targetedTokens === 'number' ? data.targetedTokens : null,
    successCount: typeof data.successCount === 'number' ? data.successCount : undefined,
    failureCount: typeof data.failureCount === 'number' ? data.failureCount : undefined,
  };
};

const convertToDate = (value: unknown): Date => {
  try {
    if (!value) {
      return new Date();
    }

    if (value instanceof Date) {
      return value;
    }

    if (typeof value === 'string' || typeof value === 'number') {
      const parsed = new Date(value);
      return isNaN(parsed.getTime()) ? new Date() : parsed;
    }

    if (value && typeof value === 'object') {
      const maybeObj = value as { toDate?: () => Date; seconds?: number; nanoseconds?: number };
      if (typeof maybeObj.toDate === 'function') {
        return maybeObj.toDate();
      }
      if (typeof maybeObj.seconds === 'number') {
        const seconds = maybeObj.seconds;
        const nanos = maybeObj.nanoseconds ?? 0;
        return new Date(seconds * 1000 + nanos / 1000000);
      }
    }

    const fallback = new Date(value as any);
    return isNaN(fallback.getTime()) ? new Date() : fallback;
  } catch (error) {
    console.warn('Failed to convert date value', value, error);
    return new Date();
  }
};

export class PushNotificationService {
  static subscribeToPushNotifications(
    callback: (records: PushNotificationRecord[]) => void,
    onError?: (error: Error) => void
  ) {
    const q = query(
      collection(db, COLLECTION),
      orderBy('sentAt', 'desc')
    );

    return onSnapshot(
      q,
      (snapshot) => {
        const records = snapshot.docs.map(mapPushNotification);
        callback(records);
      },
      (error) => {
        console.error('Push notification subscription error:', error);
        onError?.(error);
      }
    );
  }

  static async getPushNotifications(): Promise<PushNotificationRecord[]> {
    const q = query(collection(db, COLLECTION), orderBy('sentAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(mapPushNotification);
  }

  static async sendBroadcastPush(
    payload: BroadcastPushPayload
  ): Promise<{ messageId?: string; recordId?: string; targetedTokens?: number | null }> {
    const callable = httpsCallable(functions, 'sendBroadcastPush');
    const response = await callable(payload);
    return (response.data as { messageId?: string; recordId?: string; targetedTokens?: number | null }) ?? {};
  }

  static async deletePushNotification(id: string): Promise<void> {
    const callable = httpsCallable(functions, 'deleteBroadcastPush');
    await callable({ id });
  }

  static async sendUserPush(
    payload: UserPushPayload
  ): Promise<{ recordId?: string; successCount?: number; targetedTokens?: number; failureCount?: number }> {
    const callable = httpsCallable(functions, 'sendUserPush');
    const response = await callable(payload);
    return (response.data as { recordId?: string; successCount?: number; targetedTokens?: number; failureCount?: number }) ?? {};
  }
}
